/**
 * Shared product image search workflow (SerpAPI + optional remove-bg).
 * Used by Project Catalogue sandbox Edit Product and aligned with
 * ProjectCatalogFindImageButton / retail Add Product find-image UX.
 *
 * Draft-only: parent decides whether to put the URL into a form or persist.
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { notifyProductImagePreparation } from "@/lib/productImagePreparationFeedback";
import { cn } from "@/lib/utils";
import { normalizeSearchImages } from "@/components/sales/dtools/ProjectCatalogFindImageButton";
import { ImagePlus, Loader2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export type ProductFindImageSearchContext = {
  manufacturer?: string | null;
  model?: string | null;
  productName?: string | null;
  displayName?: string | null;
};

function buildSearchSeed(ctx: ProductFindImageSearchContext): string {
  const manufacturer = ctx.manufacturer?.trim() || "";
  const model = ctx.model?.trim() || "";
  const productName = ctx.productName?.trim() || "";
  const displayName = ctx.displayName?.trim() || "";

  const parts: string[] = [];
  const pushUnique = (value: string) => {
    if (!value) return;
    if (parts.some((p) => p.toLowerCase() === value.toLowerCase())) return;
    parts.push(value);
  };

  pushUnique(manufacturer);
  pushUnique(model);
  if (productName && productName.toLowerCase() !== model.toLowerCase()) {
    pushUnique(productName);
  }

  // Only append display name when it adds new identity tokens.
  if (displayName) {
    const covered = new Set(
      parts
        .join(" ")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean),
    );
    const displayTokens = displayName.toLowerCase().split(/\s+/).filter(Boolean);
    const addsNew = displayTokens.some((t) => !covered.has(t));
    if (addsNew) pushUnique(displayName);
  }

  return parts.slice(0, 3).join(" ").trim();
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchContext: ProductFindImageSearchContext;
  existingImageUrl?: string | null;
  onAccept: (imageUrl: string) => void;
  forcedQuery?: string;
  identityLabel?: string;
};

export default function ProductFindImageWorkflow({
  open,
  onOpenChange,
  searchContext,
  existingImageUrl,
  onAccept,
  forcedQuery,
  identityLabel,
}: Props) {
  const searchSeed = forcedQuery?.trim() || buildSearchSeed(searchContext);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [imageSearchText, setImageSearchText] = useState(searchSeed);
  const [manualUrl, setManualUrl] = useState(existingImageUrl ?? "");
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [finalUrl, setFinalUrl] = useState<string | null>(null);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const [showQueryEditor, setShowQueryEditor] = useState(false);
  const [searchToastShown, setSearchToastShown] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const trimmedSearchText = imageSearchText.trim();
  const searchImages = trpc.catalogue.searchProductImages.useQuery(
    { query: trimmedSearchText, limit: 20 },
    {
      enabled: open && step === 1 && trimmedSearchText.length >= 2,
      retry: false,
      refetchOnMount: "always",
    },
  );
  const removeBg = trpc.catalogue.removeImageBackground.useMutation();
  const uploadPhoto = trpc.projectCatalogueSandbox.uploadPhoto.useMutation();

  const resultImages = useMemo(
    () => normalizeSearchImages(searchImages.data?.images),
    [searchImages.data?.images],
  );

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setImageSearchText(forcedQuery?.trim() || buildSearchSeed(searchContext));
    setSelectedUrl(null);
    setFinalUrl(null);
    setManualUrl(existingImageUrl ?? "");
    setShowManualFallback(false);
    setShowQueryEditor(false);
    setSearchToastShown(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when dialog opens
  }, [open]);

  useEffect(() => {
    if (!open || step !== 1 || searchImages.isFetching || searchImages.isLoading) return;
    if (searchImages.isError) {
      if (!searchToastShown) {
        toast.error("Unable to find images for this product.");
        setSearchToastShown(true);
      }
      return;
    }
    if (searchImages.isSuccess && resultImages.length === 0 && !searchToastShown) {
      toast.message("No images found.");
      setSearchToastShown(true);
    }
  }, [
    open,
    step,
    searchImages.isFetching,
    searchImages.isLoading,
    searchImages.isError,
    searchImages.isSuccess,
    resultImages.length,
    searchToastShown,
  ]);

  const acceptFinal = (url: string) => {
    if (existingImageUrl?.trim() && existingImageUrl.trim() !== url.trim()) {
      const ok = window.confirm(
        "Replace the existing product photo with this image? You can still edit or undo before saving the product.",
      );
      if (!ok) return;
    }
    onAccept(url);
    onOpenChange(false);
    toast.success("Image applied — review and save when ready.");
  };

  const handleLocalUpload = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadPending(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const raw = String(reader.result ?? "");
          resolve(raw.includes(",") ? raw.split(",")[1] ?? "" : raw);
        };
        reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
        reader.readAsDataURL(file);
      });
      const uploaded = await uploadPhoto.mutateAsync({
        filename: file.name,
        contentType: file.type,
        base64,
      });
      notifyProductImagePreparation(uploaded);
      acceptFinal(uploaded.imageUrl);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not upload image.");
    } finally {
      setUploadPending(false);
    }
  };

  const label = [searchContext.manufacturer, searchContext.model || searchContext.productName]
    .filter(Boolean)
    .join(" ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "Find product image" : step === 2 ? "Preparing image…" : "Confirm product image"}
          </DialogTitle>
          <DialogDescription>
            {identityLabel || label || "Search results"} — pick an image, then accept it into the product form before saving.
          </DialogDescription>
        </DialogHeader>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            event.target.value = "";
            void handleLocalUpload(file);
          }}
        />
        <div className="min-h-[340px] space-y-3">
          {step === 1 ? (
            <>
              <p className="rounded-md bg-muted/40 px-2 py-1.5 text-xs text-muted-foreground" data-testid="product-find-image-query">
                Search query: {trimmedSearchText || searchSeed}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                {showQueryEditor ? (
                  <div className="flex min-w-0 flex-1 gap-2">
                    <Input value={imageSearchText} onChange={(e) => setImageSearchText(e.target.value)} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedUrl(null);
                        setSearchToastShown(false);
                        void searchImages.refetch();
                      }}
                    >
                      Search
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowQueryEditor(true)}>
                    Edit search query
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={uploadPending || uploadPhoto.isPending}
                  onClick={() => uploadInputRef.current?.click()}
                  data-testid="product-find-image-upload"
                >
                  {uploadPending || uploadPhoto.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Upload Image
                </Button>
              </div>
              {searchImages.isFetching ? (
                <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Finding product images…
                </div>
              ) : resultImages.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                  {resultImages.slice(0, 20).map((img) => (
                    <button
                      key={img.original}
                      type="button"
                      onClick={() => setSelectedUrl(img.original)}
                      className={cn(
                        "overflow-hidden rounded border bg-muted/20",
                        selectedUrl === img.original ? "border-primary ring-2 ring-primary/50" : "border-border",
                      )}
                    >
                      <img
                        src={img.thumbnail}
                        alt={img.title ?? "Search result thumbnail"}
                        className="h-24 w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2 rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
                  <p>
                    {searchImages.isError
                      ? "Unable to find images for this product."
                      : searchImages.data?.configured === false
                        ? "Image search is not configured. Paste an image URL manually."
                        : searchImages.data?.message || "No images found."}
                  </p>
                  {!showManualFallback ? (
                    <Button
                      type="button"
                      variant="link"
                      className="px-0"
                      onClick={() => setShowManualFallback(true)}
                    >
                      Paste image URL manually
                    </Button>
                  ) : null}
                </div>
              )}
              {showManualFallback ? (
                <div className="flex gap-2">
                  <Input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="https://..." />
                  <Button type="button" variant="outline" onClick={() => setSelectedUrl(manualUrl.trim() || null)}>
                    Use URL
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
          {step === 2 ? (
            <div className="flex h-56 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Preparing image…
            </div>
          ) : null}
          {step === 3 && finalUrl ? (
            <img src={finalUrl} alt="Final image preview" className="mx-auto max-h-72 rounded object-contain" />
          ) : null}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {step === 1 ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSelectedUrl(null);
                    setSearchToastShown(false);
                    void searchImages.refetch();
                  }}
                >
                  Try Again
                </Button>
                <Button
                  type="button"
                  disabled={!selectedUrl}
                  onClick={async () => {
                    if (!selectedUrl) return;
                    setStep(2);
                    try {
                      const r = await removeBg.mutateAsync({ imageUrl: selectedUrl });
                      setFinalUrl(r.imageUrl || selectedUrl);
                      notifyProductImagePreparation(r);
                    } catch {
                      setFinalUrl(selectedUrl);
                      notifyProductImagePreparation({ usedFallback: true });
                    } finally {
                      setStep(3);
                    }
                  }}
                >
                  Next
                </Button>
              </div>
            </>
          ) : null}
          {step === 3 ? (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button type="button" disabled={!finalUrl} onClick={() => finalUrl && acceptFinal(finalUrl)}>
                <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
                Use this image
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { buildSearchSeed };
